import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Package,
  UploadCloud,
  ImagePlus,
  X,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AdminLayout } from '@/components/AdminLayout';
import { productApi, categoryApi } from '@/lib/api';
import { colorApi } from '@/lib/api';
import { sizeApi } from '@/lib/api';
import { toast } from 'sonner';

const MAX_PRODUCT_IMAGES = 8;
const EMPTY_IMAGE_SLOT = '';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const fileInputRefs = useRef({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    images: [EMPTY_IMAGE_SLOT],
    stock: '100',
    featured: false,
    sizes: [],
    colors: [],
  });

  useEffect(() => {
    loadData();
    loadColors();
    loadSizes();
  }, []);

  const loadColors = async () => {
    try {
      const res = await colorApi.getAll();
      setColors(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load colors');
    }
  };

  const loadSizes = async () => {
    try {
      const res = await sizeApi.getAll();
      setSizes(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load sizes');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        productApi.getAll({ limit: 200 }),
        categoryApi.getAll(),
      ]);

      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      category_id: '',
      images: [EMPTY_IMAGE_SLOT],
      stock: '100',
      featured: false,
      sizes: [],
      colors: [],
    });
    setUploadingImages({});
    setEditingProduct(null);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        original_price: product.original_price?.toString() || '',
        category_id: product.category_id,
        images: product.images?.length ? product.images : [EMPTY_IMAGE_SLOT],
        stock: product.stock?.toString() || '0',
        featured: Boolean(product.featured),
        sizes: Array.isArray(product.sizes) ? product.sizes : [],
        colors: Array.isArray(product.colors) ? product.colors : [],
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeUploadedUrl = (value) => {
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/')) return `${window.location.origin}${value}`;
    return `${window.location.origin}/${value.replace(/^\/+/, '')}`;
  };

  const extractUploadedUrl = (data) => {
    const rawUrl =
      data?.url ||
      data?.image_url ||
      data?.imageUrl ||
      data?.file_url ||
      data?.fileUrl ||
      data?.path ||
      data?.filename ||
      data?.data?.url ||
      data?.data?.image_url ||
      '';

    return normalizeUploadedUrl(rawUrl);
  };

  const uploadProductImageToServer = async (file) => {
    const fd = new FormData();
    fd.append('image', file);

    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('megamart_token') ||
      '';

    const headers = { 'Content-Type': 'multipart/form-data' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await axios.post('/githiri/api/products/upload-image', fd, { headers });
    const uploadedUrl = extractUploadedUrl(res.data);

    if (!uploadedUrl) {
      throw new Error('Image uploaded but the server did not return a URL.');
    }

    return uploadedUrl;
  };

  const updateImage = (index, value) => {
    const updated = [...formData.images];
    updated[index] = value;
    setFormData({ ...formData, images: updated });
  };

  const removeImage = (index) => {
    const nextImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: nextImages.length ? nextImages : [EMPTY_IMAGE_SLOT],
    });
  };

  const addImage = () => {
    if (formData.images.length < MAX_PRODUCT_IMAGES) {
      setFormData({ ...formData, images: [...formData.images, EMPTY_IMAGE_SLOT] });
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large. Please upload an image below 5MB.');
      return;
    }

    try {
      setUploadingImages((prev) => ({ ...prev, [index]: true }));
      const uploadedUrl = await uploadProductImageToServer(file);
      updateImage(index, uploadedUrl);
      toast.success('Image uploaded and URL updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Image upload failed. Confirm the FastAPI upload route is installed and the server restarted.');
    } finally {
      setUploadingImages((prev) => ({ ...prev, [index]: false }));
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index].value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error('Please fill in required fields');
      return;
    }

    const invalidUrls = formData.images.filter((url) => url && !isValidUrl(url));
    if (invalidUrls.length) {
      toast.error('One or more image URLs are invalid');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      category_id: formData.category_id,
      images: formData.images.filter((i) => i.trim()),
      stock: parseInt(formData.stock, 10) || 0,
      featured: formData.featured,
      sizes: formData.sizes.filter((i) => i.trim()),
      colors: formData.colors.filter((i) => i.trim()),
    };

    try {
      setSaving(true);
      if (editingProduct) {
        await productApi.update(editingProduct.id, payload);
        toast.success('Product updated');
      } else {
        await productApi.create(payload);
        toast.success('Product created');
      }

      setConfirmDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this product?')) return;
    try {
      await productApi.delete(id);
      toast.success('Product deleted');
      loadData();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex flex-col gap-4 rounded-lg border border-stone-800/80 bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950/20 p-4 shadow-xl shadow-black/10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-400">Inventory Manager</p>
            <h1 className="mt-1 text-xl font-bold text-white">Products</h1>
            <p className="mt-1 text-sm text-stone-400">{products.length} total products in your Megamart catalogue</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="h-9 rounded-lg bg-emerald-600 px-4 font-semibold shadow-lg shadow-emerald-900/30 hover:bg-emerald-700"
            data-testid="add-product-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-stone-800 bg-stone-950/70 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 rounded-lg border-stone-700 bg-stone-900 pl-9 text-white placeholder:text-stone-500"
              data-testid="product-search"
            />
          </div>
          <Badge className="w-fit rounded-full bg-emerald-500/10 px-3 py-1 text-emerald-300">
            {filteredProducts.length} visible
          </Badge>
        </div>

        <div className="overflow-hidden rounded-lg border border-stone-800 bg-stone-950/80 shadow-2xl shadow-black/20">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead>
                <tr className="border-b border-stone-800 bg-stone-900/70">
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">Product</th>
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">Category</th>
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">Price</th>
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">Stock</th>
                  <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">Status</th>
                  <th className="p-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-stone-500">
                      Loading products...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-stone-500">
                      No products found
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-stone-800/80 transition-colors last:border-0 hover:bg-stone-900/70"
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <Package className="m-3 h-5 w-5 text-stone-600" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-white">{product.name}</p>
                            <p className="max-w-xs truncate text-sm text-stone-500">{product.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-stone-300">{product.category_name}</td>
                      <td className="p-3">
                        <p className="font-bold text-emerald-400">KES {Number(product.price || 0).toLocaleString()}</p>
                        {product.original_price && (
                          <p className="text-sm text-stone-500 line-through">KES {Number(product.original_price).toLocaleString()}</p>
                        )}
                      </td>
                      <td className="p-3 text-stone-300">{product.stock}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-2">
                          {product.featured && <Badge className="bg-purple-500/20 text-purple-300">Featured</Badge>}
                          {product.stock === 0 ? (
                            <Badge className="bg-red-500/20 text-red-300">Out of Stock</Badge>
                          ) : product.stock <= 5 ? (
                            <Badge className="bg-yellow-500/20 text-yellow-300">Low Stock</Badge>
                          ) : (
                            <Badge className="bg-green-500/20 text-green-300">In Stock</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(product)}
                            className="text-stone-400 hover:text-white"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(product.id)}
                            className="text-stone-400 hover:text-red-400"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[88vh] max-w-4xl overflow-hidden border-stone-800 bg-stone-950 p-0 text-white shadow-2xl shadow-black/50">
            <div className="sticky top-0 z-10 border-b border-stone-800 bg-gradient-to-r from-stone-950 via-stone-900 to-emerald-950/40 px-5 py-3">
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="text-xl font-bold text-white">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-stone-400">
                      Add product details, upload photos, and publish clean product data.
                    </DialogDescription>
                  </div>
                  <div className="hidden rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 sm:block">
                    <ImagePlus className="mb-1 h-5 w-5" />
                    Max {MAX_PRODUCT_IMAGES} photos
                  </div>
                </div>
              </DialogHeader>
            </div>

            <form onSubmit={handleSubmit} className="max-h-[calc(88vh-74px)] overflow-y-auto px-5 py-4">
              <div className="grid gap-4 lg:grid-cols-[1.45fr_0.55fr]">
                <div className="space-y-3 rounded-lg border border-stone-800 bg-stone-900/50 p-4">
                  <div>
                    <Label className="text-sm font-semibold text-stone-200">Product Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white placeholder:text-stone-600"
                      placeholder="e.g. Wireless Headphones"
                      data-testid="product-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-stone-200">Description</Label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1.5 min-h-[82px] w-full rounded-lg border border-stone-700 bg-stone-950 p-2.5 text-sm text-white outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Write a clear product description that helps buyers understand the item..."
                      data-testid="product-description-input"
                    />
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Price (KES) *</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white"
                        placeholder="0"
                        data-testid="product-price-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Original Price</Label>
                      <Input
                        type="number"
                        value={formData.original_price}
                        onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                        className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white"
                        placeholder="For showing discount"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Category *</Label>
                      <Select value={formData.category_id} onValueChange={(val) => setFormData({ ...formData, category_id: val })}>
                        <SelectTrigger className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white" data-testid="product-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="border-stone-700 bg-stone-900 text-white">
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id} className="text-white">
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Stock</Label>
                      <Input
                        type="number"
                        value={formData.stock}
                        onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                        className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white"
                        placeholder="0"
                        data-testid="product-stock-input"
                      />
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Product Sizes</Label>
                      <Select
                        onValueChange={(val) => {
                          if (!formData.sizes.includes(val)) {
                            setFormData({ ...formData, sizes: [...formData.sizes, val] });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent className="border-stone-700 bg-stone-900 text-white">
                          {sizes.map((size) => (
                            <SelectItem key={size.id} value={size.name} className="text-white">
                              {size.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {formData.sizes.map((size) => (
                          <Badge
                            key={size}
                            variant="secondary"
                            className="cursor-pointer rounded-full bg-stone-800 px-2 py-0.5 text-stone-200 hover:bg-red-500/20 hover:text-red-200"
                            onClick={() => setFormData({ ...formData, sizes: formData.sizes.filter((s) => s !== size) })}
                          >
                            {size} <X className="ml-1 h-3 w-3" />
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Colors</Label>
                      <Select
                        onValueChange={(val) => {
                          if (!formData.colors.includes(val)) {
                            setFormData({ ...formData, colors: [...formData.colors, val] });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1.5 h-9 rounded-lg border-stone-700 bg-stone-950 text-white">
                          <SelectValue placeholder="Select color" />
                        </SelectTrigger>
                        <SelectContent className="border-stone-700 bg-stone-900 text-white">
                          {colors.map((color) => (
                            <SelectItem key={color.id} value={color.name} className="text-white">
                              {color.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {formData.colors.map((color) => (
                          <Badge
                            key={color}
                            variant="secondary"
                            className="cursor-pointer rounded-full bg-stone-800 px-2 py-0.5 text-stone-200 hover:bg-red-500/20 hover:text-red-200"
                            onClick={() => setFormData({ ...formData, colors: formData.colors.filter((c) => c !== color) })}
                          >
                            {color} <X className="ml-1 h-3 w-3" />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 rounded-lg border border-stone-800 bg-stone-950/70 p-3">
                    <Checkbox
                      id="featured"
                      checked={formData.featured}
                      onCheckedChange={(checked) => setFormData({ ...formData, featured: !!checked })}
                      data-testid="product-featured-checkbox"
                    />
                    <div>
                      <Label htmlFor="featured" className="cursor-pointer font-semibold text-stone-200">
                        Featured Product
                      </Label>
                      <p className="text-xs text-stone-500">Show this product with highlighted placement.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 rounded-lg border border-stone-800 bg-stone-900/50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-stone-200">Photos</Label>
                      <p className="mt-1 text-xs text-stone-500">Upload item photos. URLs are filled automatically.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addImage}
                      disabled={formData.images.length >= MAX_PRODUCT_IMAGES}
                      className="rounded-lg border-stone-700 bg-stone-950 text-stone-200 hover:bg-stone-800"
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" /> Add
                    </Button>
                  </div>

                  <div className="grid gap-2">
                    {formData.images.map((img, i) => {
                      const isUploading = Boolean(uploadingImages[i]);
                      const isValidImage = img && isValidUrl(img);

                      return (
                        <div key={i} className="rounded-lg border border-stone-800 bg-stone-950/70 p-2">
                          <div className="flex gap-3">
                            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone-800 bg-stone-900">
                              {isUploading ? (
                                <Loader2 className="h-7 w-7 animate-spin text-emerald-400" />
                              ) : isValidImage ? (
                                <img src={img} alt={`Product ${i + 1}`} className="h-full w-full object-cover" />
                              ) : (
                                <ImagePlus className="h-6 w-6 text-stone-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="flex gap-2">
                                <input
                                  ref={(el) => {
                                    fileInputRefs.current[i] = el;
                                  }}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleImageUpload(i, e.target.files?.[0])}
                                />
                                <Button
                                  type="button"
                                  onClick={() => fileInputRefs.current[i]?.click()}
                                  disabled={isUploading}
                                  className="h-8 flex-1 rounded-lg bg-emerald-600 text-xs hover:bg-emerald-700"
                                >
                                  {isUploading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <UploadCloud className="mr-1 h-3.5 w-3.5" />
                                  )}
                                  {isUploading ? 'Uploading...' : 'Upload'}
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => removeImage(i)}
                                  className="h-8 w-8 rounded-lg text-red-300 hover:bg-red-500/10 hover:text-red-200"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <Input
                                value={img}
                                onChange={(e) => updateImage(i, e.target.value)}
                                className={`h-8 rounded-lg border-stone-700 bg-stone-900 text-xs text-white placeholder:text-stone-600 ${img && !isValidUrl(img) ? 'border-red-500' : ''}`}
                                placeholder="Uploaded image URL will appear here"
                              />
                              {isValidImage && (
                                <p className="flex items-center gap-1 text-xs text-emerald-300">
                                  <CheckCircle2 className="h-3.5 w-3.5" /> URL ready
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <DialogFooter className="sticky bottom-0 -mx-5 mt-4 border-t border-stone-800 bg-stone-950/95 px-5 py-2.5 backdrop-blur">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="h-9 rounded-lg border-stone-700 bg-transparent text-stone-300 hover:bg-stone-900"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || Object.values(uploadingImages).some(Boolean)}
                  className="h-9 rounded-lg bg-emerald-600 px-5 font-semibold hover:bg-emerald-700"
                  data-testid="save-product-btn"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent className="border-stone-800 bg-stone-950 text-white">
            <DialogHeader>
              <DialogTitle>Product Saved</DialogTitle>
              <DialogDescription className="text-stone-400">Do you want to add another product?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => {
                  resetForm();
                  setConfirmDialogOpen(false);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Yes, add another
              </Button>
              <Button
                onClick={() => {
                  setConfirmDialogOpen(false);
                  setDialogOpen(false);
                  loadData();
                }}
                variant="secondary"
              >
                No, close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
